'use client';

import { DeleteOutlined } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, CircularProgress, Dialog, Grid } from '@mui/material';
import {
	DataGridPro,
	GridActionsCellItem,
	GridColDef,
	GridRowId,
	GridRowModel,
	GridRowModes,
	GridRowModesModel,
	GridRowsProp,
} from '@mui/x-data-grid-pro';
import { useLazyQuery, useMutation } from '@parkchirp/api-client';
import {
	ExceedingAmountDecisionEnum,
	ParkerStatusHistoryActionEnum,
	TerminationRuleEnum,
} from 'parkchirp-shared/enums';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
	ADMIN_TERMINATE_PARKER,
	CREATE_PARKER_STATUS_HISTORY,
	DELETE_PARKER_STATUS_HISTORY,
	UPDATE_PARKER_STATUS_HISTORY,
} from '../../../mutations';
import { GET_PARKER_STATUS_HISTORY } from '../../../queries';
import { SetShowSnackbar } from '../../../types';
import {
	datagridFilterPanelAttributes,
	dayjs,
	formatDateForDatePicker,
	formatDateParkerStatus,
	formatDateParkerStatusForDisplay,
	isOwnerPortal,
	shouldShowProrationModalForUpdateTerminationPendingParkers,
} from '../../../utils';
import { EditorModal } from '../../modals/editorModal';
import { ParkerTerminationErrorModal } from '../parkerTerminationErrorModal';
import { ParkerTerminationModal } from '../parkerTerminationModal';
import { parkerStatusDetailColumns } from './parkerStatusDetailColumns.config';

type StatusData = {
	getParkerStatusHistory: {
		id: string;
		createdAt: string;
		updatedAt: string;
		action: string;
		startDate: string;
		endDate: string;
		notes: string;
		skipProration: boolean;
	}[];
};

interface ActionStatusProps {
	parkerId: string;
	parkerLoading: boolean;
	currentDate: Date;
	setRefreshKey: (key: any) => void;
	setShowSnackbar: SetShowSnackbar;
	theme: any;
	tenantSubscriptionId?: string;
	billingDay?: number;
	terminationNoticeDay?: number;
	terminationRule?: string;
	refetchParkersFromCache?: () => void;
	isWaitlistParker: boolean;
}

export const StatusDetail: FC<ActionStatusProps> = ({
	parkerId,
	parkerLoading,
	currentDate,
	setRefreshKey,
	setShowSnackbar,
	tenantSubscriptionId,
	billingDay,
	terminationNoticeDay,
	terminationRule,
	refetchParkersFromCache,
	theme,
	isWaitlistParker,
}) => {
	const [showTerminationConfirm, setShowTerminationConfirm] = useState<boolean>(false);
	const [createNewRowOnModalClose, setCreateNewRowOnModalClose] = useState<boolean>(true);
	const [showTerminationUpdateConfirm, setShowTerminationUpdateConfirm] = useState<boolean>(false);
	const [showTerminationDeleteConfirm, setShowTerminationDeleteConfirm] = useState<boolean>(false);
	const [showManualAdjustmentAlert, setShowManualAdjustmentAlert] = useState<boolean>(false);
	const [showTerminationErrorModal, setShowTerminationErrorModal] = useState<boolean>(false);
	// This hook is only used when we are showing a modal to the user to confirm
	// a parker's termination. The date confirmed in the modal is stored in this variable
	// and is used as the termination date when terminating an active parker.
	const [confirmedTerminationDate, setConfirmedTerminationDate] = useState<dayjs.Dayjs | null>(null);
	const [editActionMode, setEditActionMode] = useState<boolean>(false);
	const [editingRowIds, setEditingRowIds] = useState<any[]>([]);
	const [currentRowId, setCurrentRowId] = useState<string | null>(null);
	const [hasActionChange, setHasActionChange] = useState<boolean>(false);
	const [dataId, setDataId] = useState<string | null>(null);
	const [statusRows, setStatusRows] = useState<GridRowsProp>([]);
	const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
	const [initialRow, setInitialRow] = useState({} as GridRowModel);
	const [updateActionMode, setUpdateActionMode] = useState<boolean>(false);

	const [getParkerStatusHistory, { data: statusHistoryData, loading: statusHistoryLoading }] =
		useLazyQuery<StatusData>(GET_PARKER_STATUS_HISTORY);

	useEffect(() => {
		if (parkerId) {
			getParkerStatusHistory({
				variables: {
					parkerId,
					tenantSubscriptionId,
				},
				context: {
					apiVersion: 'v2',
				},
				fetchPolicy: 'network-only',
			});
		}
	}, [parkerId, getParkerStatusHistory, tenantSubscriptionId]);

	useEffect(() => {
		if (statusHistoryData) {
			const fetchedStatusRows = statusHistoryData.getParkerStatusHistory.map((status) => ({
				id: status.id,
				dateRecorded: formatDateParkerStatusForDisplay(status.createdAt),
				action:
					ParkerStatusHistoryActionEnum[status.action] === 'Parker Start Date'
						? 'Start Date'
						: ParkerStatusHistoryActionEnum[status.action],
				startDate: formatDateParkerStatusForDisplay(status.startDate),
				suspensionStartDate: status.action === 'SUSPEND' ? status.startDate : null,
				suspensionEndDate: status.action === 'SUSPEND' ? status.endDate : null,
				noEndDate: status.action === 'SUSPEND' ? status.endDate === null : null,
				terminationDate: status.action === 'TERMINATE' ? status.startDate : null,
				notes: status.notes || '',
				skipProration: status.skipProration,
				editable: false,
			}));

			const topRows = fetchedStatusRows.filter((row) => row.action === 'Start Date');
			const otherRows = fetchedStatusRows.filter((row) => row.action !== 'Start Date');

			otherRows.sort((a, b) => dayjs(a.dateRecorded).unix() - dayjs(b.dateRecorded).unix());

			const sortedStatusRows = [...topRows, ...otherRows];

			setStatusRows(sortedStatusRows);
		}
	}, [statusHistoryData]);

	useEffect(() => {
		if (!showTerminationConfirm) {
			setShowManualAdjustmentAlert(false);
		}
	}, [showTerminationConfirm]);

	const handleActionInfoUpdate = useCallback((id: GridRowId, field: string, value: any) => {
		setStatusRows((preStatuses) =>
			preStatuses.map((status) => {
				if (status.id !== id) {
					return status;
				}
				const updatedStatus = {
					...status,
					[field]: value,
				};

				return updatedStatus;
			}),
		);
	}, []);

	const handleKeyDown = useCallback((event) => {
		if (event.key === ' ') {
			event.stopPropagation();
		}
	}, []);

	const handleAddAction = useCallback(() => {
		const id = uuidv4();

		setStatusRows((oldRows) => [
			...oldRows,
			{
				id,
				dateRecorded: formatDateParkerStatusForDisplay(currentDate),
				action: '',
				startDate: '',
				suspensionStartDate: '',
				suspensionEndDate: '',
				noEndDate: null,
				terminationDate: null,
				notes: '',
				isNew: true,
			},
		]);

		setRowModesModel((oldModel) => ({
			...oldModel,
			[id]: { mode: GridRowModes.Edit, fieldToFocus: 'action' },
		}));

		setEditingRowIds((prevIds) => [...prevIds, id]);
		setCurrentRowId(id);
		setEditActionMode(true);
	}, [currentDate]);

	const handleCancelEdit = useCallback(
		(rowId) => {
			if (hasActionChange) {
				setShowSnackbar({
					message: 'There are unsaved changes on your page, press cancel again to discard changes.',
					severity: 'error',
					autoHideDuration: 3000,
					onClose: () => {
						setShowSnackbar(null);
						setHasActionChange(false);
					},
				});
			} else {
				setRowModesModel({
					...rowModesModel,
					[rowId]: { mode: GridRowModes.View, ignoreModifications: true },
				});

				const editedRow = statusRows.find((row) => row.id === rowId);
				if (editedRow?.isNew) {
					setStatusRows(statusRows.filter((row) => row.id !== rowId));
				} else {
					setStatusRows(
						statusRows.map((row) =>
							row.id === rowId
								? {
										...initialRow,
									}
								: row,
						),
					);
				}
				setEditingRowIds((prevIds) => prevIds.filter((id) => id !== rowId));
				setCurrentRowId(null);

				setEditActionMode(false);
				setHasActionChange(false);
			}
		},
		[hasActionChange, setShowSnackbar, initialRow, rowModesModel, statusRows],
	);

	const [createParkerStatusHistory, { loading: createLoading }] = useMutation(CREATE_PARKER_STATUS_HISTORY, {
		onCompleted: () => {
			setShowSnackbar({
				message: 'Action added',
				severity: 'success',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			setHasActionChange(false);
			setEditActionMode(false);
		},
		refetchQueries: [
			{
				query: GET_PARKER_STATUS_HISTORY,
				variables: {
					parkerId,
					tenantSubscriptionId,
				},
			},
			'getParkerStatusHistory',
		],
	});

	const [adminTerminateParker, { loading: terminateLoading }] = useMutation(ADMIN_TERMINATE_PARKER, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		onCompleted: () => {
			setShowSnackbar({
				message: 'Termination Saved',
				severity: 'success',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			setHasActionChange(false);
			setEditActionMode(false);
			setUpdateActionMode(false);
		},
		refetchQueries: [
			{
				query: GET_PARKER_STATUS_HISTORY,
				variables: {
					parkerId,
					tenantSubscriptionId,
				},
			},
			'getParkerStatusHistory',
		],
	});

	const [updateParkerStatusHistory, { loading: updateLoading }] = useMutation(UPDATE_PARKER_STATUS_HISTORY, {
		onCompleted: () => {
			setShowSnackbar({
				message: 'Action updated',
				severity: 'success',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			setHasActionChange(false);
			setEditActionMode(false);
			setUpdateActionMode(false);
		},
		refetchQueries: [
			{
				query: GET_PARKER_STATUS_HISTORY,
				variables: {
					parkerId,
					tenantSubscriptionId,
				},
			},
			'getParkerStatusHistory',
		],
	});

	const [deleteParkerStatusHistory, { loading: deleteLoading }] = useMutation(DELETE_PARKER_STATUS_HISTORY, {
		onCompleted: () => {
			setShowSnackbar({
				message: 'Action deleted',
				severity: 'success',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
		},
		refetchQueries: [
			{
				query: GET_PARKER_STATUS_HISTORY,
				variables: {
					parkerId,
					tenantSubscriptionId,
				},
			},
			'GetParkerStatusHistory',
		],
	});

	const isLastDayOfMonth = (date: Date): boolean => {
		const tomorrow = dayjs(date).add(1, 'day');
		return tomorrow.date() === 1;
	};

	const getDateList = (today: Date): { firstDays: Date[]; lastDays: Date[] } => {
		const monthsFirstDays: Date[] = [];
		const monthLastDays: Date[] = [];
		const startForFirstDays = today.getDate() === 1 ? 0 : 1;
		const startForLastDays = isLastDayOfMonth(today) ? 1 : 0;

		for (let i = startForFirstDays; i < 18 + startForFirstDays; i++) {
			const firstDay = dayjs(today).add(i, 'month').startOf('month').toDate();
			monthsFirstDays.push(firstDay);
		}

		for (let i = startForLastDays; i < 18 + startForLastDays; i++) {
			const lastDay = dayjs(today)
				.add(i + 1, 'month')
				.date(0)
				.toDate();
			monthLastDays.push(lastDay);
		}

		return { firstDays: monthsFirstDays, lastDays: monthLastDays };
	};

	const { firstDays, lastDays } = getDateList(currentDate);

	const yesterday = dayjs(currentDate).subtract(1, 'day').toDate();

	const terminationRecord = statusRows?.find(
		(status) => (status.action === 'TERMINATE' || status.action === 'Terminate') && status.id !== currentRowId,
	);
	const suspensionRecords = statusRows?.filter(
		(status) => (status.action === 'SUSPEND' || status.action === 'Suspend') && status.id !== currentRowId,
	);

	const handleValidation = useCallback(
		(row) => {
			const rowSuspensionStart = dayjs(row?.suspensionStartDate).toDate();
			const rowSuspensionEnd = dayjs(row?.suspensionEndDate).toDate();

			const validations = [
				{
					message: `Please select an Action`,
					failingCondition: !row?.action,
				},
				{
					message: `Please provide a Suspension Start Date`,
					failingCondition: row?.action === 'Suspend' && !row?.suspensionStartDate,
				},
				{
					message: `Please provide a Suspension End Date`,
					failingCondition: row?.action === 'Suspend' && !row?.noEndDate && !row?.suspensionEndDate,
				},
				{
					message: `Suspension End Date cannot be before Suspension Start Date`,
					failingCondition:
						row?.suspensionEndDate &&
						!row?.noEndDate &&
						dayjs(row?.suspensionStartDate).isAfter(dayjs(row?.suspensionEndDate)),
				},
				{
					message: `Suspension Start date must be before the start date of Termination actions`,
					failingCondition:
						row?.action === 'Suspend' &&
						terminationRecord &&
						dayjs(row?.suspensionStartDate).isAfter(dayjs(terminationRecord.terminationDate)),
				},
				{
					message: `A parker cannot have overlapping suspension periods`,
					failingCondition:
						row?.action === 'Suspend' &&
						suspensionRecords?.some((record) => {
							const recordSuspensionStart = dayjs(record.suspensionStartDate);
							const recordSuspensionEnd = dayjs(record.suspensionEndDate);
							return (
								(dayjs(rowSuspensionStart).isSameOrAfter(recordSuspensionStart) &&
									dayjs(rowSuspensionStart).isSameOrBefore(record.suspensionEndDate)) ||
								(dayjs(rowSuspensionEnd).isSameOrAfter(recordSuspensionStart) &&
									dayjs(rowSuspensionEnd).isSameOrBefore(recordSuspensionEnd)) ||
								(dayjs(rowSuspensionStart).isSameOrBefore(recordSuspensionStart) &&
									dayjs(rowSuspensionEnd).isSameOrAfter(recordSuspensionEnd))
							);
						}),
				},
				{
					message: `Invalid Termination Date`,
					failingCondition:
						row?.action === 'Terminate' && (!row?.terminationDate || row?.terminationDate < yesterday),
				},
			];
			const failingValidations = validations.filter(({ failingCondition }) => failingCondition);
			if (failingValidations.length) {
				setShowSnackbar({
					message: failingValidations[0].message,
					severity: 'error',
					autoHideDuration: 3000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
				return false;
			}
			return true;
		},
		[setShowSnackbar, suspensionRecords, terminationRecord, yesterday],
	);

	const validateAndSubmitNewHistoryRow = async (
		skipProration = false,
		exceedingAmountDecision: ExceedingAmountDecisionEnum = null,
	) => {
		const row = statusRows.find(({ id }) => id === currentRowId);
		if (confirmedTerminationDate) {
			row.terminationDate = confirmedTerminationDate.toDate();
		}
		if (handleValidation(row)) {
			try {
				if (row.action === 'Terminate') {
					await adminTerminateParker({
						variables: {
							parkerId,
							terminationDate: formatDateParkerStatus(row.terminationDate),
							skipProration,
							exceedingAmountDecision,
							notes: row.notes,
						},
					});
				} else {
					const { data } = await createParkerStatusHistory({
						variables: {
							tenantSubscriptionId,
							parker_id: parkerId,
							action: 'SUSPEND',
							start_date: formatDateParkerStatus(row.suspensionStartDate),
							end_date: row.suspensionEndDate ? formatDateParkerStatus(row.suspensionEndDate) : null,
							notes: row.notes || '',
							timezone: dayjs.tz.guess(),
							skipProration,
							exceedingAmountDecision,
						},
					});
					setDataId(data.createParkerStatusHistory.id);
				}

				setHasActionChange(false);
				setEditActionMode(false);
				setRowModesModel({ ...rowModesModel, [row.id]: { mode: GridRowModes.View } });
				setEditingRowIds(editingRowIds.filter((id) => id !== row.id));
				setCurrentRowId(null);
				setRefreshKey((prevKey) => prevKey + 1);
				setShowTerminationConfirm(false);
				setShowTerminationErrorModal(false);
				if (refetchParkersFromCache) {
					await refetchParkersFromCache();
				}
			} catch (error) {
				if (error.message?.includes('exceeds')) {
					setShowTerminationConfirm(false);
					setShowTerminationErrorModal(true);
					return;
				}
				setShowSnackbar({
					message: error.message,
					severity: 'error',
					autoHideDuration: 3000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			}
		}
	};

	const validateAndUpdateHistoryRow = async (
		skipProration = null,
		exceedingAmountDecision: ExceedingAmountDecisionEnum = null,
	) => {
		const row = statusRows.find(({ id }) => id === currentRowId);
		if (handleValidation(row)) {
			try {
				const { action, suspensionStartDate, suspensionEndDate, terminationDate, notes, timezone } = row;
				let skipProrationToUse = null;
				if (row.skipProration !== null) {
					skipProrationToUse = row.skipProration;
				}
				if (skipProration !== null) {
					skipProrationToUse = skipProration;
				}

				if (action === 'Terminate') {
					await adminTerminateParker({
						variables: {
							parkerId,
							terminationDate: formatDateParkerStatus(formatDateForDatePicker(terminationDate)),
							skipProration: skipProrationToUse !== false,
							exceedingAmountDecision,
							notes,
						},
					});
				} else {
					await updateParkerStatusHistory({
						variables: {
							tenantSubscriptionId,
							id: dataId || row.id,
							parker_id: parkerId,
							action: 'SUSPEND',
							start_date: formatDateParkerStatus(formatDateForDatePicker(suspensionStartDate)),
							end_date:
								action === 'Suspend' && suspensionEndDate
									? formatDateParkerStatus(formatDateForDatePicker(suspensionEndDate))
									: null,
							notes: notes || '',
							...(skipProrationToUse !== null && { skipProration: skipProrationToUse }),
							timezone,
							exceedingAmountDecision,
						},
					});
				}

				setHasActionChange(false);
				setEditActionMode(false);
				setRowModesModel({ ...rowModesModel, [row.id]: { mode: GridRowModes.View } });
				setEditingRowIds(editingRowIds.filter((id) => id !== row.id));
				setCurrentRowId(null);
				setShowTerminationConfirm(false);
				setShowTerminationUpdateConfirm(false);
				setShowTerminationErrorModal(false);
				setRefreshKey((prevKey) => prevKey + 1);
				if (refetchParkersFromCache) {
					await refetchParkersFromCache();
				}
			} catch (error) {
				if (error.message?.includes('exceeds')) {
					setShowTerminationConfirm(false);
					setShowTerminationUpdateConfirm(false);
					setShowTerminationErrorModal(true);
					return;
				}
				setShowSnackbar({
					message: error.message,
					severity: 'error',
					autoHideDuration: 3000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			}
		}
	};

	const handleSaveAction = async () => {
		const row = statusRows.find(({ id }) => id === currentRowId);
		if (
			row &&
			row.action.toUpperCase() === 'TERMINATE' &&
			[TerminationRuleEnum.DAILY_PRORATION, TerminationRuleEnum.HALF_MONTH].includes(
				terminationRule as TerminationRuleEnum,
			)
		) {
			setConfirmedTerminationDate(dayjs(row.terminationDate));
			setShowTerminationConfirm(true);
			return;
		}
		// if the termination rule is daily and we are terminating
		// show the termination date input modal,
		// after confirming a date, call this thing.
		// else just resume normal
		await validateAndSubmitNewHistoryRow();
	};

	const handleUpdateAction = async () => {
		const row = statusRows.find(({ id }) => id === currentRowId);
		// this is the new date that the user specified in the row that they wanted to update
		const terminationDate = dayjs.utc(row.terminationDate);
		const whenAutoInvoiceIsSentForMonthOfTermination = dayjs(terminationDate)
			.subtract(1, 'month')
			.set('date', billingDay);
		const now = dayjs();
		const newTerminationDateInvoiceHasBeenSent = whenAutoInvoiceIsSentForMonthOfTermination.isBefore(now);
		const originalTerminationDate = dayjs.utc(row.startDate, 'MM/DD/YYYY');
		const whenAutoInvoiceIsSentForOriginalTermination = dayjs(originalTerminationDate)
			.subtract(1, 'month')
			.set('date', billingDay);
		const invoiceHasBeenSentWithOriginalTermination = whenAutoInvoiceIsSentForOriginalTermination.isBefore(now);
		const terminationDateWasMovedBetweenMonths = originalTerminationDate.month() !== terminationDate.month();
		if (
			row &&
			row.action.toUpperCase() === 'TERMINATE' &&
			[TerminationRuleEnum.DAILY_PRORATION, TerminationRuleEnum.HALF_MONTH].includes(
				terminationRule as TerminationRuleEnum,
			)
		) {
			// give the user another chance to prorate the invoice
			if (
				shouldShowProrationModalForUpdateTerminationPendingParkers(
					invoiceHasBeenSentWithOriginalTermination,
					terminationDateWasMovedBetweenMonths,
					newTerminationDateInvoiceHasBeenSent,
					!!row.skipProration,
				)
			) {
				setShowManualAdjustmentAlert(invoiceHasBeenSentWithOriginalTermination && !row.skipProration);
				setConfirmedTerminationDate(terminationDate);
				setCreateNewRowOnModalClose(false);
				return setShowTerminationConfirm(true);
			}
			return setShowTerminationUpdateConfirm(true);
		}
		return validateAndUpdateHistoryRow();
	};

	const handleEditAction = useCallback(
		(rowId) => () => {
			const currentRow = statusRows.find((row) => row.id === rowId);

			if (!currentRow.isNew) {
				setInitialRow({ ...currentRow });
			}
			setRowModesModel({ ...rowModesModel, [rowId]: { mode: GridRowModes.Edit } });
			setCurrentRowId(rowId);
			setHasActionChange(true);
			setEditingRowIds((prevIds) => [...prevIds, rowId]);
			setEditActionMode(true);
			setUpdateActionMode(true);
		},
		[rowModesModel, statusRows],
	);

	const handleDeleteAction = useCallback(
		async (rowId) => {
			try {
				await deleteParkerStatusHistory({
					variables: {
						id: dataId || rowId,
						tenantSubscriptionId,
						parkerId,
					},
				});
				setShowTerminationDeleteConfirm(false);
				setStatusRows(statusRows.filter((row) => row.id !== rowId));
				if (refetchParkersFromCache) {
					await refetchParkersFromCache();
				}
				return setRefreshKey((prevKey) => prevKey + 1);
			} catch (error) {
				return setShowSnackbar({
					message: error.message,
					severity: 'error',
					autoHideDuration: 3000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			}
		},
		[
			dataId,
			deleteParkerStatusHistory,
			parkerId,
			refetchParkersFromCache,
			setRefreshKey,
			setShowSnackbar,
			statusRows,
			tenantSubscriptionId,
		],
	);

	const deleteOrShowConfirmationModal = useCallback(
		(rowId) => {
			setCurrentRowId(rowId);

			const row = statusRows.find((statusRow) => statusRow.id === rowId);
			const terminationDate = dayjs(row.terminationDate);
			const whenAutoInvoiceIsSentForMonthOfTermination = dayjs(terminationDate)
				.subtract(1, 'month')
				.set('date', billingDay);
			const now = dayjs();
			const invoiceHasBeenSent = whenAutoInvoiceIsSentForMonthOfTermination.isBefore(now);
			if (
				row?.action &&
				row.action.toUpperCase() === 'TERMINATE' &&
				[TerminationRuleEnum.DAILY_PRORATION, TerminationRuleEnum.HALF_MONTH].includes(
					terminationRule as TerminationRuleEnum,
				) &&
				!row.skipProration &&
				invoiceHasBeenSent
			) {
				return setShowTerminationDeleteConfirm(true);
			}
			return handleDeleteAction(rowId);
		},
		[billingDay, handleDeleteAction, statusRows, terminationRule],
	);

	const processRowUpdate = useCallback(
		(newRow: GridRowModel) => {
			const updatedRow = { ...newRow, isNew: false };
			setStatusRows(statusRows.map((row) => (row.id === newRow.id ? updatedRow : row)));
			return updatedRow;
		},
		[statusRows],
	);

	const handleRowModesModelChange = useCallback((newRowModesModel: GridRowModesModel) => {
		setRowModesModel(newRowModesModel);
	}, []);

	const renderEditAction = useCallback(
		(rowId) => {
			const currentSuspensionRecord = suspensionRecords.find((sr) => sr.id === rowId);
			const suspensionEndDate = currentSuspensionRecord?.suspensionEndDate;
			const terminationStartDate = terminationRecord?.terminationDate;

			const canEditSuspension =
				!editActionMode &&
				(dayjs.utc(suspensionEndDate).isSameOrAfter(dayjs.utc(currentDate), 'day') ||
					suspensionEndDate === null);
			const canEditTermination =
				!editActionMode &&
				terminationStartDate !== null &&
				dayjs.utc(currentDate).startOf('day').isSameOrBefore(dayjs.utc(terminationStartDate).startOf('day'));

			if (currentSuspensionRecord && canEditSuspension) {
				return (
					<GridActionsCellItem
						key={`edit-${rowId}`}
						icon={<EditIcon />}
						label='Edit'
						onClick={handleEditAction(rowId)}
					/>
				);
			}

			if (!currentSuspensionRecord && canEditTermination) {
				return (
					<GridActionsCellItem
						key={`edit-${rowId}`}
						icon={<EditIcon />}
						label='Edit'
						onClick={handleEditAction(rowId)}
					/>
				);
			}

			return null;
		},
		[handleEditAction, editActionMode, currentDate, suspensionRecords, terminationRecord],
	);

	const renderDeleteAction = useCallback(
		(rowId) => {
			const targetRow = statusRows.find((row) => row.id === rowId);
			if (
				!editActionMode &&
				targetRow &&
				(dayjs(targetRow.suspensionStartDate).isAfter(currentDate) ||
					dayjs
						.utc(targetRow.terminationDate)
						.startOf('day')
						.isSameOrAfter(dayjs.utc(currentDate).startOf('day')))
			) {
				return (
					<GridActionsCellItem
						key={`delete-${rowId}`}
						icon={<DeleteOutlined />}
						label='Delete'
						onClick={() => deleteOrShowConfirmationModal(rowId)}
					/>
				);
			}
			return null;
		},
		[statusRows, editActionMode, currentDate, deleteOrShowConfirmationModal],
	);

	const statusColumns: GridColDef[] = parkerStatusDetailColumns({
		editActionMode,
		editingRowIds,
		setHasActionChange,
		handleActionInfoUpdate,
		currentDate,
		firstDays,
		lastDays,
		yesterday,
		handleKeyDown,
		renderEditAction,
		renderDeleteAction,
		updateActionMode,
		isWaitlistParker,
	});

	const somethingIsLoading =
		parkerLoading || createLoading || updateLoading || deleteLoading || statusHistoryLoading || terminateLoading;

	return (
		<Grid className='pca__parkerDetail-status' item xs={12} sx={{ mt: 1, mb: 1 }}>
			<h2>Parker Status</h2>
			<DataGridPro
				sx={{ mt: 2, mb: 2 }}
				rows={statusRows}
				columns={statusColumns}
				sortModel={[{ field: 'dateRecorded', sort: 'asc' }]}
				loading={statusHistoryLoading || deleteLoading || null}
				getRowId={(row) => row.id}
				isCellEditable={(params) => editingRowIds.includes(params.id)}
				editMode='row'
				rowModesModel={rowModesModel}
				onRowModesModelChange={handleRowModesModelChange}
				processRowUpdate={processRowUpdate}
				slotProps={{ filterPanel: { ...datagridFilterPanelAttributes } }}
				autoHeight
				disableRowSelectionOnClick
				disableColumnReorder
				disableColumnMenu
				hideFooter
			/>
			<Grid item xs={12} sx={{ mt: 1 }}>
				{!editActionMode && !terminationRecord && !isOwnerPortal && !isWaitlistParker && (
					<Button variant='contained' onClick={handleAddAction} size='small' disabled={somethingIsLoading}>
						Add New Action
					</Button>
				)}
				{editActionMode && (
					<Box className='buttons' sx={{ display: 'flex', justifyContent: 'flex-end', gap: '20px' }}>
						<Button
							sx={{ border: '1px solid' }}
							size='small'
							variant='outlined'
							onClick={() => handleCancelEdit(currentRowId)}
						>
							Cancel
						</Button>
						<Button
							variant='contained'
							size='small'
							color='success'
							onClick={
								statusRows.find((row) => row.id === currentRowId)?.isNew
									? () => handleSaveAction()
									: () => handleUpdateAction()
							}
						>
							{createLoading || updateLoading ? (
								<CircularProgress size={25} sx={{ color: 'white' }} />
							) : statusRows.find((row) => row.id === currentRowId)?.isNew ? (
								'Save'
							) : (
								'Update'
							)}
						</Button>
					</Box>
				)}
			</Grid>

			{showTerminationConfirm && (
				<Dialog open maxWidth='lg' sx={{ padding: '10px' }}>
					<ParkerTerminationModal
						handleClose={() => setShowTerminationConfirm(false)}
						handleSave={(skipProration) => {
							if (createNewRowOnModalClose) {
								validateAndSubmitNewHistoryRow(skipProration);
								return;
							}
							validateAndUpdateHistoryRow(skipProration);
							setCreateNewRowOnModalClose(true);
						}}
						showManualAdjustmentAlert={showManualAdjustmentAlert}
						effectiveDate={confirmedTerminationDate}
						setEffectiveDate={setConfirmedTerminationDate}
						terminationRule={terminationRule}
						terminationNoticeDay={terminationNoticeDay}
						containerClassName='parker-termination-confirm-modal'
						loading={createLoading || updateLoading}
						theme={theme}
					/>
				</Dialog>
			)}
			{showTerminationErrorModal && (
				<Dialog open maxWidth='lg' sx={{ padding: '10px' }}>
					<ParkerTerminationErrorModal
						{...{
							handleClose: () => setShowTerminationErrorModal(false),
							loading: createLoading || updateLoading,
							handleSave: (descision: ExceedingAmountDecisionEnum) => {
								if (createNewRowOnModalClose) {
									validateAndSubmitNewHistoryRow(false, descision);
									return;
								}
								validateAndUpdateHistoryRow(false, descision);
								setCreateNewRowOnModalClose(true);
							},
							containerClassName: 'parker-termination-confirm-modal',
							theme,
						}}
					/>
				</Dialog>
			)}
			{showTerminationUpdateConfirm && (
				<Dialog open maxWidth='lg' sx={{ padding: '10px' }}>
					<EditorModal
						loading={false}
						handleClose={() => setShowTerminationUpdateConfirm(null)}
						handleSave={() => validateAndUpdateHistoryRow(true)}
						headerText='Update termination date?'
						containerClassName='parker-termination-confirm-modal'
						theme={theme}
					>
						<p>
							The monthly invoices for this parker must be manually adjusted to correct the proration
							amount.
						</p>
					</EditorModal>
				</Dialog>
			)}
			{showTerminationDeleteConfirm && (
				<Dialog open maxWidth='lg' sx={{ padding: '10px' }}>
					<EditorModal
						loading={deleteLoading}
						handleClose={() => setShowTerminationDeleteConfirm(null)}
						handleSave={() => handleDeleteAction(currentRowId)}
						headerText='Delete termination from parker history?'
						containerClassName='parker-termination-confirm-modal'
						theme={theme}
					>
						<p>
							The monthly invoices for this parker must be manually adjusted to correct the proration
							amount.
						</p>
					</EditorModal>
				</Dialog>
			)}
		</Grid>
	);
};
